/**
 * ============================================================================
 * RevenueBreakdownSection - SPORTS REVENUE WITH COURT BREAKDOWN
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. TWO DISPLAY MODES
 *    - No sport selected: Show all sports with progress bars
 *    - Sport selected: Show selected sport summary + court breakdown
 *    - Driven by selectedSportId prop
 *
 * 2. COURT BREAKDOWN
 *    - Each sport has optional court_breakdown array
 *    - Only shown when specific sport is selected
 *    - Shows performance of individual courts within that sport
 *
 * 3. PROGRESS BARS
 *    - Use percentage field from API
 *    - Color rotation for visual distinction
 *    - Always show percentage + booking count
 *
 * 4. FILTERING
 *    - Parent manages selectedSportId state
 *    - Dropdown at top controls selection
 *    - Changes trigger parent callback
 *
 * ============================================================================
 */

import React from 'react';
import { formatCurrency } from '../../utils/revenueFormatters';
import type { SportRevenue } from '../../types/revenue';

interface RevenueBreakdownSectionProps {
  sportsRevenue: SportRevenue[];
  selectedSportId: number | null;
  onSportChange: (sportId: number | null) => void;
}

/**
 * Revenue breakdown by sport with optional court details
 */
export function RevenueBreakdownSection({
  sportsRevenue,
  selectedSportId,
  onSportChange,
}: RevenueBreakdownSectionProps) {
  // Color palette for progress bars
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-indigo-500',
  ];

  // Get selected sport data (-1 is used as sentinel for memberships)
  const selectedSport = selectedSportId
    ? selectedSportId === -1
      ? sportsRevenue.find((s) => s.type === 'membership')
      : sportsRevenue.find((s) => s.sport_id === selectedSportId)
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Revenue by Sport</h3>

        {/* Sport Filter Dropdown */}
        <select
          value={selectedSportId ?? 'all'}
          onChange={(e) => {
            const val = e.target.value;
            onSportChange(val === 'all' ? null : Number(val));
          }}
          className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500 bg-white"
        >
          <option value="all">All Sports</option>
          {sportsRevenue.map((sport) => (
            <option key={sport.sport_id ?? -1} value={sport.sport_id ?? -1}>
              {sport.sport_name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {sportsRevenue.length > 0 ? (
          selectedSport ? (
            // SELECTED SPORT VIEW: Show sport summary + court breakdown
            <>
              {/* Sport Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedSport.sport_name}
                    </p>
                    <p className="text-sm font-normal text-gray-600">
                      {selectedSport.bookings} total {selectedSport.type === 'membership' ? 'memberships' : 'bookings'}
                    </p>
                  </div>
                  <p className="text-xl font-semibold text-gray-900">
                    {formatCurrency(selectedSport.revenue)}
                  </p>
                </div>
              </div>

              {/* Court Breakdown */}
              {selectedSport.court_breakdown && selectedSport.court_breakdown.length > 0 ? (
                <>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Court Performance</p>
                  {selectedSport.court_breakdown.map((court, idx) => {
                    const color = colors[idx % colors.length];
                    return (
                      <div key={court.court_id}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${color}`}></div>
                            <span className="font-semibold text-gray-900">
                              {court.court_name}
                            </span>
                            <span className="text-gray-500 text-sm">
                              ({court.bookings} booking{court.bookings !== 1 ? 's' : ''})
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">
                              {formatCurrency(court.revenue)}
                            </p>
                            <p className="text-sm text-gray-500">{court.percentage}%</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`${color} h-2 rounded-full transition-all`}
                            style={{ width: `${court.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No court data available</p>
                </div>
              )}
            </>
          ) : (
            // ALL SPORTS VIEW: Show all sports with progress bars
            sportsRevenue.map((sport, idx) => {
              const color = colors[idx % colors.length];
              return (
                <div key={sport.sport_id}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${color}`}></div>
                      <span className="font-semibold text-gray-900">{sport.sport_name}</span>
                      <span className="text-gray-500 text-sm">
                        ({sport.bookings} {sport.type === 'membership' ? `membership${sport.bookings !== 1 ? 's' : ''}` : `booking${sport.bookings !== 1 ? 's' : ''}`}
                        {sport.courts ? `, ${sport.courts} court${sport.courts !== 1 ? 's' : ''}` : ''})
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        {formatCurrency(sport.revenue)}
                      </p>
                      <p className="text-sm text-gray-500">{sport.percentage}%</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`${color} h-2 rounded-full transition-all`}
                      style={{ width: `${sport.percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })
          )
        ) : (
          // Empty state
          <div className="text-center py-8">
            <p className="text-gray-600">No revenue data yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Revenue breakdown will appear as bookings are completed
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
